// Copyright 1999-2017. Plesk International GmbH. All rights reserved.

import { PopupForm } from './popup-form';
import { DynamicPopupHint } from './dynamic-popup-hint';
import { List } from './list';
import { Panel } from './panel';
import { prepareUrl, getComponent, addStatusMessage } from './common';
import { redirect } from './form-redirect';

/**
 * @class Jsw.ReporterPopup
 * @extends Jsw.PopupForm
 */
export const ReporterPopup = Class.create(PopupForm, {
    _initConfiguration($super, config) {
        config = Object.extend({
            singleRowButtons: true,
        }, config || {});
        $super(config);
        this._actionCollectId = `${this._id}-action-collect`;
        this._actionToSupportId = `${this._id}-action-to-support`;
        this._sendButtonId = 'btn-send';
        this._cancelButtonId = 'btn-cancel';
        this._supportUrl = this._getConfigParam('supportUrl', '');
    },

    render($super) {
        $super();

        this.setBoxType('form-box');
        this.setTitle(this.lmsg('title'));

        this.addRightButton(this.lmsg('buttonCollect'), this._onCollectClick, true, true, { id: this._sendButtonId });
        this.addRightButton(this.lmsg('buttonCancel'), this._onCancelClick, false, false, { id: this._cancelButtonId });
    },

    _initComponentElement($super) {
        $super();

        this._componentElement.down('div.content-area').update(
            '<div class="form-row">' +
                '<div class="single-row">' +
                '<div class="field-value">' +
                    '<label>' +
                        `<input class="radio" type="radio" name="action" id="${this._actionCollectId}" checked="checked"> ` +
                        `${this.lmsg('actionCollect')}${this._getTooltip()}` +
                    '</label>' +
                '</div>' +
                '<div class="field-value">' +
                    `<label><input class="radio" type="radio" name="action" id="${this._actionToSupportId}"> ${this.lmsg('actionToSupport')}</label>` +
                '</div>' +
                '</div>' +
                '</div>' +
                `<a href="#" class="toggler js-show-previous-reports">${this.lmsg('showPreviousReports')}</a>` +
                `<a href="#" class="toggler hidden js-hide-previous-reports">${this.lmsg('hidePreviousReports')}</a>` +
                `<div id="js-reports"><span class="ajax-loading hidden">${this.lmsg('pleaseWait')}</span></div>`
        );
    },

    _onSupportClick(close) {
        window.open(this._supportUrl, '_blank');
        this._disableSendButton(this.lmsg('buttonWait'));
        const context = this;
        new Ajax.Request(
            prepareUrl('/admin/report/report-usage/'), {
                method: 'post',
                onException: this._onException.bind(this),
                onSuccess() {
                    close ? context.hide() : context._enableSendButton(context.lmsg('buttonToSupport'));
                },
            }
        );
    },

    _onCollectClick() {
        this._disableSendButton();
        DynamicPopupHint.unset();
        $(this._contentAreaId).update(`<p class="text-center"><span class="ajax-loading">${this.lmsg('collecting')}${this._getTooltip()}</span></p>`);
        this._addTooltips();
        const context = this;
        new Ajax.Request(
            prepareUrl('/admin/report/collect/'), {
                method: 'post',
                onException: this._onException.bind(this),
                onSuccess(response) {
                    const data = response.responseText.evalJSON();
                    if (context._checkResponseStatus(data)) {
                        DynamicPopupHint.unset();
                        const link = `<a href="${prepareUrl(`/admin/report/download/file/${encodeURIComponent(data.file)}`)}">${context.lmsg('downloadLink')}</a>`;
                        $(context._contentAreaId).update(
                            `<p>${context.lmsg('collectedDescription')}</p>` +
                            `<p>${context.lmsg('collectedDownload', { link })}</p>` +
                            `<p>${context.lmsg('collectedConsent')}</p>`
                        );
                        context._enableSendButton(context.lmsg('buttonSend'), context._onSendClick.bind(context, data.file));
                    }
                },
            }
        );
    },

    _onSendClick(file) {
        this._disableSendButton(this.lmsg('buttonSending'));
        const context = this;
        new Ajax.Request(
            prepareUrl('/admin/report/send/'), {
                method: 'post',
                parameters: { file },
                onException: this._onException.bind(this),
                onSuccess(response) {
                    const data = response.responseText.evalJSON();
                    if (context._checkResponseStatus(data)) {
                        $(context._contentAreaId).update(`<p>${context.lmsg('sentInfo', { id: data.reportId.escapeHTML() })}</p>`);
                        context._enableSendButton(context.lmsg('buttonToSupport'), context._onSupportClick.bind(context, false));
                    }
                },
            }
        );
    },

    _onCancelClick() {
        this.hide();
    },

    _onShowReportsClick(e) {
        e.preventDefault();
        this._componentElement.down('.js-show-previous-reports').addClassName('hidden');
        if (getComponent('reports-list') && this._componentElement.down('.list-box')) {
            this._componentElement.down('.list-box').removeClassName('hidden');
            this._componentElement.down('.js-hide-previous-reports').removeClassName('hidden');
            return;
        }
        this._componentElement.down('.ajax-loading').removeClassName('hidden');
        const dialogHeight = this._componentElement.getHeight();
        const list = new List({
            id: 'reports-list',
            dataUrl: prepareUrl('/admin/report/list-data'),
            pageable: false,
            columns: [
                {
                    header: this.lmsg('date'),
                    dataIndex: 'date',
                },
                {
                    header: this.lmsg('reportId'),
                    dataIndex: 'id',
                },
            ],
            onRedraw: function () {
                this._componentElement.down('.js-hide-previous-reports').removeClassName('hidden');
                this._componentElement.down('.ajax-loading').addClassName('hidden');
                const maxHeight = Math.max(document.viewport.getScrollOffsets().top + document.viewport.getHeight() -
                    (parseInt(this._componentElement.getStyle('top')) * 1.5) - dialogHeight, 100);
                this._componentElement.down('.list').setStyle({ 'max-height': `${parseInt(maxHeight)}px` });
            }.bind(this),
        });
        new Panel({
            cls: 'list-box',
            renderTo: 'js-reports',
            items: [
                list,
            ],
        });
    },

    _onHideReportsClick(e) {
        e.preventDefault();
        this._componentElement.down('.list-box').addClassName('hidden');
        this._componentElement.down('.js-show-previous-reports').removeClassName('hidden');
        this._componentElement.down('.js-hide-previous-reports').addClassName('hidden');
    },

    _addEvents($super) {
        $super();

        $(this._actionCollectId).observe('click', function () {
            this._enableSendButton(this.lmsg('buttonCollect'), this._onCollectClick.bind(this));
        }.bind(this));
        $(this._actionToSupportId).observe('click', function () {
            this._enableSendButton(this.lmsg('buttonToSupport'), this._onSupportClick.bind(this, true));
        }.bind(this));
        this._componentElement.down('.js-show-previous-reports').observe('click', this._onShowReportsClick.bind(this));
        this._componentElement.down('.js-hide-previous-reports').observe('click', this._onHideReportsClick.bind(this));
    },

    _addTooltips($super) {
        $super();

        new DynamicPopupHint.Instance({
            title: this.lmsg('hintTitle'),
            target: $('tooltip'),
            placement: 'right',
            content: this.lmsg('hintText'),
        });
    },

    _getTooltip() {
        if (!this._tooltip) {
            this._tooltip = `&nbsp;<a class="nowrap" id="tooltip">${this.lmsg('hintLink')}</a>`;
        }
        return this._tooltip;
    },

    _disableSendButton(title) {
        $(this._sendButtonId).firstDescendant().disable();
        $(this._sendButtonId).addClassName('disabled');
        if (title) {
            $(this._sendButtonId).down('button').update(`<span class="wait">${title}</span>`);
        }
    },

    _enableSendButton(title, handler) {
        if (handler) {
            $(this._sendButtonId).down('button').stopObserving('click');
            $(this._sendButtonId).down('button').observe('click', handler);
        }
        if (title) {
            $(this._sendButtonId).down('button').update(title);
        }

        $(this._sendButtonId).firstDescendant().enable();
        $(this._sendButtonId).removeClassName('disabled');
    },

    _checkResponseStatus(response) {
        if (response.redirect) {
            redirect(response.redirect);
        }

        if (response.status && 'success' != response.status) {
            $A(response.statusMessages).each(function (message) {
                addStatusMessage(message.status, message.content);
            });
            this.hide();
            return false;
        }
        return true;
    },

    _onException(transport, exception) {
        addStatusMessage('error', `Internal error: ${exception}`);
        this.hide();
    },
});



// WEBPACK FOOTER //
// ./jsw/issue-reporter.js